import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Alert } from 'antd';
import { profileService } from '../api/services/profileService';
import { showToast } from '../utils/toast';
import { useLogout } from '../hooks/useLogout';
import { useAuth } from '../hooks/useAuth';

export default function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { clearAllStorage } = useAuth();

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [notifications, setNotifications] = useState({
    messages: true,
    listings: true,
    promotions: false,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem('ap_notif_prefs');
      if (saved) setNotifications(JSON.parse(saved));
    } catch {}
  }, []);

  const toggleNotif = (key: keyof typeof notifications) => {
    const updated = { ...notifications, [key]: !notifications[key] };
    setNotifications(updated);
    localStorage.setItem('ap_notif_prefs', JSON.stringify(updated));
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== t('settings.delete_confirm_word')) {
      showToast.error(t('settings.must_match'));
      return;
    }
    try {
      setDeleting(true);
      await profileService.deleteAccount();
      showToast.success(t('settings.delete_account'));
      clearAllStorage();
      navigate('/');
    } catch (error: any) {
      showToast.error(error?.message || t('common.error_try_again'));
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
      setConfirmText('');
    }
  };

  const currentLang = i18n.language?.split('-')[0] || 'sq';

  const Section: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
    <div className="dash-panel shadow-sm mb-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="dash-stat-icon" style={{ background: 'rgba(254,90,29,0.1)', color: 'var(--color1)', width: 44, height: 44, fontSize: 18, borderRadius: 10 }}>
          <i className={`bi ${icon}`} />
        </div>
        <h5 className="fw-bold mb-0">{title}</h5>
      </div>
      {children}
    </div>
  );

  const RowItem: React.FC<{ label: string; sublabel?: string; action: React.ReactNode }> = ({ label, sublabel, action }) => (
    <div className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: '1px solid var(--dash-border)' }}>
      <div>
        <h6 className="mb-1 fw-bold text-dark">{label}</h6>
        {sublabel && <p className="mb-0 text-muted" style={{ fontSize: 13 }}>{sublabel}</p>}
      </div>
      {action}
    </div>
  );

  const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
    <button
      onClick={onChange}
      style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: checked ? 'var(--color1)' : '#cbd5e1',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: checked ? 22 : 2,
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }} />
    </button>
  );

  return (
    <div style={{ maxWidth: 800 }}>
      <div className="mb-4">
        <h2 className="fw-bold mb-1">{t('settings.title')}</h2>
        <p className="text-muted mb-4">{t('settings.subtitle')}</p>
      </div>

      <div className="row">
        <div className="col-12">
          {/* Account */}
          <Section icon="bi-person-circle" title={t('settings.account_settings')}>

            <RowItem
              label={t('settings.my_listings_manager')}
              sublabel={t('settings.my_listings_manager_sub')}
              action={<button className="btn btn-outline-secondary btn-sm px-3 fw-bold rounded-pill" onClick={() => navigate('/dashboard/ads')}>{t('settings.view_btn')}</button>}
            />
            <RowItem
              label={t('settings.credits_balance')}
              sublabel={t('settings.credits_balance_sub')}
              action={<button className="btn btn-outline-secondary btn-sm px-3 fw-bold rounded-pill" onClick={() => navigate('/dashboard/credits')}>{t('settings.view_btn')}</button>}
            />
            <div className="pt-3">
              <button className="btn btn-light text-danger fw-bold rounded-pill px-4 py-2 d-flex align-items-center gap-2" onClick={logout}>
                <i className="bi bi-box-arrow-right" /> {t('settings.logout')}
              </button>
            </div>
          </Section>



          {/* Notifications */}
          <Section icon="bi-bell" title={t('settings.notification_prefs')}>
            <RowItem label={t('settings.in_app_messaging')} sublabel={t('settings.in_app_messaging_sub')} action={<Toggle checked={notifications.messages} onChange={() => toggleNotif('messages')} />} />
            <RowItem label={t('settings.listing_status')} sublabel={t('settings.listing_status_sub')} action={<Toggle checked={notifications.listings} onChange={() => toggleNotif('listings')} />} />
            <RowItem label={t('settings.marketing_emails')} sublabel={t('settings.marketing_emails_sub')} action={<Toggle checked={notifications.promotions} onChange={() => toggleNotif('promotions')} />} />
          </Section>

          {/* Danger Zone */}
          <Section icon="bi-exclamation-triangle" title={t('settings.danger_zone')}>
            <div className="p-4 rounded-3" style={{ background: '#fff5f5', border: '1px solid #ffcccc' }}>
              <h6 className="fw-bold text-danger mb-2">{t('settings.delete_account_perm')}</h6>
              <p className="text-muted small mb-4">{t('settings.deletion_desc', 'This action cannot be undone. All listings, messages, and associated data will be lost forever.')}</p>
              <button 
                className="btn btn-danger fw-bold px-4 rounded-pill"
                onClick={() => { setDeleteModalVisible(true); setConfirmText(''); }}
              >
                <i className="bi bi-trash me-2" /> {t('settings.delete_btn')}
              </button>
            </div>
          </Section>
        </div>
      </div>

      <Modal
        title={<span className="fw-bold text-danger"><i className="bi bi-exclamation-circle me-2" /> {t('settings.confirm_title')}</span>}
        open={deleteModalVisible}
        onCancel={() => { setDeleteModalVisible(false); setConfirmText(''); }}
        footer={null} centered width={500}
      >
        <Alert message={t('settings.confirm_permanent')} description={t('settings.confirm_desc')} type="error" showIcon style={{ marginBottom: 20 }} />
        <p className="fw-bold mb-2">{t('settings.type_to_confirm')} <code>{t('settings.delete_confirm_word')}</code>:</p>
        <Input
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder={t('settings.type_placeholder')}
          size="large"
          status={confirmText && confirmText !== t('settings.delete_confirm_word') ? 'error' : ''}
          style={{ marginBottom: 8 }}
        />
        {confirmText && confirmText !== t('settings.delete_confirm_word') && (
          <p className="text-danger small mb-3">{t('settings.must_match')}</p>
        )}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => { setDeleteModalVisible(false); setConfirmText(''); }}>
            {t('settings.cancel')}
          </button>
          <button
            className="btn rounded-pill px-4 fw-bold"
            disabled={confirmText !== t('settings.delete_confirm_word') || deleting}
            onClick={handleDeleteAccount}
            style={{
              background: '#dc3545', color: '#fff', border: 'none',
              opacity: confirmText !== t('settings.delete_confirm_word') ? 0.5 : 1,
            }}
          >
            {deleting ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-trash me-2" />}
            {t('settings.delete_account')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
